import React, { useEffect, useState } from "react";
import { toast } from "@/hooks/use-toast";

import { DesktopSidebar } from "@/components/layout/DesktopSidebar";
import { DesktopTopBar } from "@/components/layout/DesktopTopBar";
import { MobileTopHeader } from "@/components/layout/MobileTopHeader";
import { MobileBottomNav } from "@/components/layout/MobileBottomNav";
import { MoreSheet } from "@/components/layout/MoreSheet";
import { NotificationsSheet } from "@/components/notifications/NotificationsSheet";
import { useMediaQuery } from "@/hooks/use-media-query";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";

const SIDEBAR_STORAGE_KEY = "cb.sidebarCollapsed";

export function AppShell({ children }) {
  const isMobile = useMediaQuery("(max-width: 1023px)");
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [notifCursor, setNotifCursor] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_STORAGE_KEY) === "1";
    } catch { return false; }
  });

  useEffect(() => {
    try { localStorage.setItem(SIDEBAR_STORAGE_KEY, sidebarCollapsed ? "1" : "0"); } catch {}
  }, [sidebarCollapsed]);

  useEffect(() => {
    if (!isMobile) setMoreOpen(false);
  }, [isMobile]);

  // Safeguard against the mobile "tap once before you can scroll" freeze
  useEffect(() => {
    const clearStuckLocks = () => {
      const hasOpenOverlay = document.querySelector(
        '[role="dialog"][data-state="open"], [data-radix-popper-content-wrapper], [data-state="open"][data-radix-dialog-content]'
      );
      if (!hasOpenOverlay) {
        const b = document.body;
        if (b.style.pointerEvents === "none") b.style.pointerEvents = "";
        if (b.hasAttribute("data-scroll-locked")) b.removeAttribute("data-scroll-locked");
        if (b.style.overflow === "hidden") b.style.overflow = "";
      }
    };
    clearStuckLocks();
    const id = window.setInterval(clearStuckLocks, 1000);
    return () => window.clearInterval(id);
  }, [location.pathname]);

  useEffect(() => {
    let mounted = true;

    async function loadCount() {
      try {
        const res = await api.get("/notifications");
        if (!mounted) return;
        setUnread(res.data?.unread_count || 0);
        const newest = (res.data?.items || [])[0]?.created_at;
        if (newest && !notifCursor) setNotifCursor(newest);
      } catch {}
    }

    async function pollToasts() {
      try {
        if (!notifCursor) return;
        const res = await api.get(`/notifications/after/${encodeURIComponent(notifCursor)}`);
        const items = res.data?.items || [];
        if (!items.length) return;
        const newest = items[items.length - 1]?.created_at;
        if (newest) setNotifCursor(newest);
        items.forEach((n) => { toast({ title: n.title || "Notification", description: n.message || "" }); });
      } catch {}
    }

    loadCount();
    const countTimer = window.setInterval(loadCount, 60000);
    const toastTimer = window.setInterval(pollToasts, 15000);
    return () => { mounted = false; window.clearInterval(countTimer); window.clearInterval(toastTimer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notifCursor]);

  useEffect(() => {
    if (!notifOpen) return;
    (async () => { try { const res = await api.get("/notifications"); const newest = (res.data?.items || [])[0]?.created_at; if (newest) setNotifCursor(newest); } catch {} })();
  }, [notifOpen]);

  const sidebarWidth = sidebarCollapsed ? 64 : 220;

  return (
    <div
      data-testid="app-shell"
      className="min-h-screen bg-white text-zinc-900 dark:bg-black dark:text-white"
    >
      <NotificationsSheet open={notifOpen} onOpenChange={setNotifOpen} onCountsChange={setUnread} />
      {isMobile && (
        <MoreSheet open={moreOpen} onOpenChange={setMoreOpen} />
      )}

      {isMobile ? (
        <>
          <MobileTopHeader
            onOpenNotifications={() => setNotifOpen(true)}
            unreadCount={unread}
          />
          <main data-testid="app-main">
            <div className="mx-auto w-full px-4 pt-4 pb-[calc(12svh+env(safe-area-inset-bottom,0px)+12px)]">
              {children}
            </div>
          </main>
          <MobileBottomNav onOpenMore={() => setMoreOpen(true)} moreActive={moreOpen} />
        </>
      ) : (
        <>
          <DesktopSidebar
            collapsed={sidebarCollapsed}
            onToggleCollapse={() => setSidebarCollapsed((v) => !v)}
          />
          <div
            style={{ marginLeft: sidebarWidth }}
            className="flex min-h-screen flex-col transition-[margin-left] duration-200 ease-out"
          >
            <DesktopTopBar
              collapsed={sidebarCollapsed}
              onExpand={() => setSidebarCollapsed(false)}
              onOpenNotifications={() => setNotifOpen(true)}
              unreadCount={unread}
            />
            <main data-testid="app-main" className="flex-1">
              <div className="mx-auto w-full px-6 lg:px-8 py-6">
                {children}
              </div>
            </main>
          </div>
        </>
      )}
    </div>
  );
}
